import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import moment from "moment-timezone";
import TextField from "../../volunteer_dashboard/domains/global/TextField";
import { FrequencyTypes, RepeatTypes, WeekdaySort } from "./reducer";
import * as actions from "./actions";
import {
  getDayOfYearFormatted,
  getLocalizedDayOfWeek,
  getOrdinalWeekOfMonth,
} from "../rrule";
import { connectShift } from "./context";

const basicFreqOptions = {
  none: "none",
  custom: "custom",
  ...FrequencyTypes,
};
const customMonthOptions = {
  on: "on the",
  each: "each",
};
const customMonthOnIntervalOptions = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  last: -1,
};
const StyledRadio = withStyles({
  root: {
    "&$checked": {
      color: "#274653",
    },
    marginLeft: "8px",
  },
  checked: {},
})(Radio);
const StyledDialog = withStyles({
  paper: {
    padding: 20,
  },
})(Dialog);
export const FrequencyForm = ({
  startDate,
  startTime,
  endTime,
  rrule,
  customText,
  dispatch,
  hiddenFields = false,
  startingValues = null,
}) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [basicFrequency, setBasicFrequency] = useState(
    startingValues?.rrule ? basicFreqOptions.custom : basicFreqOptions.none,
  );
  const [basicEnds, setBasicEnds] = useState(RepeatTypes.forever);
  const [endsOn, setEndsOn] = useState(moment().format("YYYY-MM-DD"));
  const [endsAfter, setEndsAfter] = useState(0);
  const [customFrequency, setCustomFrequency] = useState(FrequencyTypes.daily);
  const [customInterval, setCustomInterval] = useState(1);
  const [monthlyType, setMonthlyType] = useState(customMonthOptions.on);
  const [monthInterval, setMonthInterval] = useState(
    customMonthOnIntervalOptions.first,
  );
  const [weekByDays, setWeekByDays] = useState([]);
  const [monthOnThe, setMonthOnThe] = useState("SU,MO,TU,WE,TH,FR,SA");
  const [monthDays, setMonthDays] = useState([]);
  const [customEnds, setCustomEnds] = useState(RepeatTypes.forever);
  const [customEndsOn, setCustomEndsOn] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [customEndsAfter, setCustomEndsAfter] = useState(0);
  const showRepeat =
    basicFrequency !== basicFreqOptions.none &&
    basicFrequency !== basicFreqOptions.custom;
  useEffect(() => {
    if (!startingValues) {
      return;
    }
    dispatch(actions.setDate(startingValues.date));
    dispatch(actions.setStart(startingValues.start));
    dispatch(actions.setEnd(startingValues.end));
    dispatch(actions.setRrule(startingValues.rrule));
  }, []);
  useEffect(() => {
    if (basicFrequency === basicFreqOptions.custom) {
      return;
    }
    const data = {
      frequency: basicFrequency,
      repeatType:
        basicFrequency === basicFreqOptions.none ? RepeatTypes.none : basicEnds,
      endsOn,
      endsAfter,
    };
    dispatch(actions.updateBasicForm(data));
    dispatch(actions.calculateRrule());
  }, [basicFrequency, basicEnds, endsOn, endsAfter]);
  useEffect(() => {
    if (rrule && basicFrequency === basicFreqOptions.custom) {
      dispatch(actions.fetchCustomText(rrule));
    } else {
      dispatch(actions.setCustomText(t("shiftForm.frequencyCustomLabel")));
    }
  }, [rrule]);
  useEffect(() => {
    if (!hiddenFields) {
      return;
    }
    document.getElementById("hidden-date").value = startDate;
    document.getElementById("hidden-start").value = startTime;
    document.getElementById("hidden-end").value = endTime;
    document.getElementById("hidden-rrule").value = rrule;
  }, [startDate, startTime, endTime, rrule]);
  const submitCustom = () => {
    const byDays =
      customFrequency === FrequencyTypes.monthly
        ? monthOnThe
        : weekByDays.sort((a, b) => WeekdaySort[a] - WeekdaySort[b]).join(",");
    const byMonthDays = monthDays.sort((a, b) => a - b);
    const data = {
      customMonthType: monthlyType,
      frequency: customFrequency,
      repeatType: customEnds,
      interval: customInterval,
      bySetPos: monthInterval,
      byDays,
      byMonthDays,
      endsOn: customEndsOn,
      endsAfter: customEndsAfter,
    };
    dispatch(actions.customFrequency(data));
    dispatch(actions.calculateRrule());
    setShowDialog(false);
  };
  const cancelCustom = () => {
    setShowDialog(false);
  };
  const openCustom = () => {
    setShowDialog(true);
  };
  const dateChange = (v) => {
    dispatch(actions.setDate(v));
    if (basicFrequency !== basicFreqOptions.custom) {
      dispatch(actions.calculateRrule());
    }
  };
  const handleWeeklyChecks = (val) => {
    const index = weekByDays.indexOf(val);
    if (index > -1) {
      weekByDays.splice(index, 1);
    } else {
      weekByDays.push(val);
    }
    setWeekByDays([...weekByDays]);
  };
  const handleMonthlyChecks = (val) => {
    const index = monthDays.indexOf(val);
    if (index > -1) {
      monthDays.splice(index, 1);
    } else {
      monthDays.push(val);
    }
    setMonthDays([...monthDays]);
  };
  const monthRows = useMemo(() => {
    const arr = [];
    for (let i = 1; i < 32; i += 1) {
      arr.push(
        <label htmlFor={`${i}`} className="picker__container" key={i}>
          <input
            id={`${i}`}
            name={`${i}`}
            type="checkbox"
            value={i}
            onChange={() => handleMonthlyChecks(`${i}`)}
            checked={monthDays.includes(`${i}`)}
          />
          <span className="picker__indicator">{i}</span>
        </label>,
      );
    }
    return arr;
  }, [monthDays]);
  const weekRows = useMemo(() => {
    const arr = [];
    [
      [t("rRule.sundayCode"), "SU"],
      [t("rRule.mondayCode"), "MO"],
      [t("rRule.tuesdayCode"), "TU"],
      [t("rRule.wednesdayCode"), "WE"],
      [t("rRule.thursdayCode"), "TH"],
      [t("rRule.fridayCode"), "FR"],
      [t("rRule.saturdayCode"), "SA"],
    ].forEach((val) => {
      arr.push(
        <label htmlFor={val[1]} className="picker__container" key={val[1]}>
          <input
            id={val[1]}
            name={val[1]}
            type="checkbox"
            value={val[1]}
            onChange={() => handleWeeklyChecks(val[1])}
            checked={weekByDays.includes(val[1])}
          />
          <span className="picker__indicator">{val[0].charAt(0)}</span>
        </label>,
      );
    });
    return arr;
  }, [weekByDays]);
  return (
    <>
      <StyledDialog
        open={showDialog}
        onClose={cancelCustom}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          {t("rRule.customRecurrence")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={3} style={{ alignSelf: "center" }}>
              {t("rRule.repeatEvery")}
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="interval"
                type="number"
                variant="outlined"
                value={customInterval}
                onChange={setCustomInterval}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="customFrequency"
                variant="outlined"
                value={customFrequency}
                onChange={setCustomFrequency}
                select
              >
                <MenuItem value={FrequencyTypes.daily}>
                  {t("rRule.dayDays")}
                </MenuItem>
                <MenuItem value={FrequencyTypes.weekly}>
                  {t("rRule.weekWeeks")}
                </MenuItem>
                <MenuItem value={FrequencyTypes.monthly}>
                  {t("rRule.monthMonths")}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={1} />
            {customFrequency === FrequencyTypes.weekly && (
              <>
                <Grid item xs={12} style={{ alignSelf: "center" }}>
                  {t("rRule.repeatOn")}
                </Grid>
                <Grid item xs={1} />
                <Grid item xs={11} className="picker picker__week">
                  {weekRows}
                </Grid>
              </>
            )}
            {customFrequency === FrequencyTypes.monthly && (
              <>
                <Grid item xs={1} />
                <Grid item xs={2} style={{ alignSelf: "center" }}>
                  {t("rRule.repeat")}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    id="monthlyType"
                    variant="outlined"
                    value={monthlyType}
                    onChange={setMonthlyType}
                    select
                  >
                    <MenuItem value={customMonthOptions.on}>
                      {t("rRule.onThe")}
                    </MenuItem>
                    <MenuItem value={customMonthOptions.each}>
                      {t("rRule.each")}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={5} />
                {monthlyType === customMonthOptions.on ? (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        id="monthOnInterval"
                        variant="outlined"
                        value={monthInterval}
                        onChange={setMonthInterval}
                        select
                      >
                        <MenuItem value={customMonthOnIntervalOptions.first}>
                          {t("rRule.first")}
                        </MenuItem>
                        <MenuItem value={customMonthOnIntervalOptions.second}>
                          {t("rRule.second")}
                        </MenuItem>
                        <MenuItem value={customMonthOnIntervalOptions.third}>
                          {t("rRule.third")}
                        </MenuItem>
                        <MenuItem value={customMonthOnIntervalOptions.fourth}>
                          {t("rRule.fourth")}
                        </MenuItem>
                        <MenuItem value={customMonthOnIntervalOptions.fifth}>
                          {t("rRule.fifth")}
                        </MenuItem>
                        <MenuItem value={customMonthOnIntervalOptions.last}>
                          {t("rRule.last")}
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={4}>
                      <TextField
                        id="byDays"
                        variant="outlined"
                        value={monthOnThe}
                        onChange={setMonthOnThe}
                        select
                      >
                        <MenuItem value="SU,MO,TU,WE,TH,FR,SA">
                          {t("rRule.day")}
                        </MenuItem>
                        <MenuItem value="MO,TU,WE,TH,FR">
                          {t("rRule.weekday")}
                        </MenuItem>
                        <MenuItem value="SU,SA">
                          {t("rRule.weekendDay")}
                        </MenuItem>
                        <MenuItem value="SU">{t("rRule.sunday")}</MenuItem>
                        <MenuItem value="MO">{t("rRule.monday")}</MenuItem>
                        <MenuItem value="TU">{t("rRule.tuesday")}</MenuItem>
                        <MenuItem value="WE">{t("rRule.wednesday")}</MenuItem>
                        <MenuItem value="TH">{t("rRule.thursday")}</MenuItem>
                        <MenuItem value="FR">{t("rRule.friday")}</MenuItem>
                        <MenuItem value="SA">{t("rRule.saturday")}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={4} />
                  </>
                ) : (
                  <>
                    <Grid item xs={2} />
                    <div className="picker picker__month">{monthRows}</div>
                  </>
                )}
              </>
            )}
            <Grid item xs={12}>
              <FormLabel style={{ color: "#274653" }} component="legend">
                {t("shiftForm.frequencyRepeatEndLabel")}
              </FormLabel>
            </Grid>
            <RadioGroup
              aria-label="ends"
              name="ends1"
              value={customEnds}
              onChange={(_, v) => setCustomEnds(v)}
            >
              <FormControlLabel
                value="forever"
                control={<StyledRadio />}
                label={t("shiftForm.frequencyRepeatEndsNever")}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} />
                <Grid item xs={4}>
                  <FormControlLabel
                    value={RepeatTypes.endsOn}
                    control={<StyledRadio />}
                    label={t("shiftForm.frequencyRepeatEndsOn")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="endsOn"
                    type="date"
                    variant="outlined"
                    value={customEndsOn}
                    onChange={setCustomEndsOn}
                    disabled={customEnds !== RepeatTypes.endsOn}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} />
                <Grid item xs={4}>
                  <FormControlLabel
                    value={RepeatTypes.endsAfter}
                    control={<StyledRadio />}
                    label={t("shiftForm.frequencyRepeatEndsAfter")}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    id="endsAfter"
                    type="number"
                    variant="outlined"
                    value={customEndsAfter}
                    onChange={setCustomEndsAfter}
                    disabled={customEnds !== RepeatTypes.endsAfter}
                  />
                </Grid>
                <Grid item xs={3} style={{ alignSelf: "center" }}>
                  {t("shiftForm.frequencyRepeatForOccurrences")}
                </Grid>
              </Grid>
            </RadioGroup>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={12} />
            <Grid item xs={8}>
              <Button
                onClick={submitCustom}
                variant="contained"
                color="primary"
                disableElevation
                style={{ marginLeft: "15px" }}
              >
                {t("global.submit")}
              </Button>
              <Button
                onClick={cancelCustom}
                disableElevation
                style={{ backgroundColor: "transparent", marginLeft: "0.5em" }}
              >
                {t("global.cancel")}
              </Button>
            </Grid>
            <Grid item xs={4} />
            <Grid item xs={12} />
          </Grid>
        </DialogActions>
      </StyledDialog>
      <Grid item xs={3}>
        <TextField
          id="date"
          label={t("shiftForm.dateLabel")}
          type="date"
          variant="outlined"
          value={startDate}
          onChange={dateChange}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          id="start"
          variant="outlined"
          type="time"
          value={startTime}
          onChange={(v) => dispatch(actions.setStart(v))}
        />
      </Grid>
      <Grid item xs={1} style={{ textAlign: "center", alignSelf: "center" }}>
        {t("shiftForm.timeRangeJoiner")}
      </Grid>
      <Grid item xs={2}>
        <TextField
          id="end"
          variant="outlined"
          type="time"
          value={endTime}
          onChange={(v) => dispatch(actions.setEnd(v))}
        />
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={8}>
        <TextField
          id="mdc-select--rrule-frequency-select"
          label={t("shiftForm.frequencyLabel")}
          variant="outlined"
          value={basicFrequency}
          onChange={setBasicFrequency}
          select
        >
          <MenuItem className="mdc-list-item" value={basicFreqOptions.none}>
            {t("rRule.doesNotRepeat")}
          </MenuItem>
          <MenuItem className="mdc-list-item" value={basicFreqOptions.daily}>
            {t("rRule.daily")}
          </MenuItem>
          <MenuItem
            className="mdc-list-item"
            value={basicFreqOptions.weekly}
          >{`${t("shiftForm.frequencyWeeklyOnDayPrefix")} ${getLocalizedDayOfWeek(startDate)}`}</MenuItem>
          <MenuItem
            className="mdc-list-item"
            value={basicFreqOptions.monthly}
          >{`${t("shiftForm.frequencyMonthlyOnDayPrefix")} ${getOrdinalWeekOfMonth(startDate)} ${getLocalizedDayOfWeek(startDate)}`}</MenuItem>
          <MenuItem
            className="mdc-list-item"
            value={basicFreqOptions.yearly}
          >{`${t("shiftForm.frequencyYearlyOnDatePrefix")} ${getDayOfYearFormatted(startDate)}`}</MenuItem>
          <MenuItem className="mdc-list-item" value={basicFreqOptions.weekday}>
            {t("shiftForm.frequencyEveryWeekday")}
          </MenuItem>
          <MenuItem
            onClick={openCustom}
            className="mdc-list-item"
            value={basicFreqOptions.custom}
          >
            {customText}
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={4} />
      {showRepeat && (
        <>
          <Grid item xs={8}>
            <TextField
              id="until"
              label={t("shiftForm.frequencyRepeatUntilLabel")}
              variant="outlined"
              value={basicEnds}
              onChange={setBasicEnds}
              select
            >
              <MenuItem value={RepeatTypes.forever}>
                {t("shiftForm.frequencyRepeatUntilForever")}
              </MenuItem>
              <MenuItem value={RepeatTypes.endsOn}>
                {t("shiftForm.frequencyRepeatUntilDate")}
              </MenuItem>
              <MenuItem value={RepeatTypes.endsAfter}>
                {t("shiftForm.frequencyRepeatNumOfShifts")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={4} />
        </>
      )}
      {basicEnds !== RepeatTypes.none && basicEnds !== RepeatTypes.forever && (
        <>
          <Grid item xs={8}>
            {showRepeat && basicEnds === RepeatTypes.endsOn && (
              <TextField
                id="until"
                label={t("shiftForm.dateLabel")}
                type="date"
                variant="outlined"
                value={endsOn}
                onChange={setEndsOn}
              />
            )}
            {showRepeat && basicEnds === RepeatTypes.endsAfter && (
              <TextField
                id="count"
                label={t("shiftForm.frequencyRepeatNumOfShiftsLabel")}
                type="number"
                variant="outlined"
                value={endsAfter}
                onChange={setEndsAfter}
              />
            )}
          </Grid>
          <Grid item xs={4} />
        </>
      )}
    </>
  );
};

export default connectShift(FrequencyForm);
